// src/components/ManageGame/PlayerList.js
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import Button from '../Button/Button';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { useAlertDialog } from '../ConfirmDialog/ConfirmDialogProvider';
import profilePic from '../../assets/images/profile_image.jpg';
import { fetchGame, updateAttendance, movePlayer, updatePlayerPaymentStatus } from '../../store/slices/gameSlice';
import './PlayerList.css';

/**
 * PlayerList handles:
 * 1) Drag–drop re-ordering / cross-team swaps (all phases).
 * 2) Attendance toggling (phase 2) - keyed by player.email (stable).
 * 3) Persisting team layout to the backend on every successful drag.
 */

function clampDropIndex(team, requestedIndex) {
  const safeTeamLength = Array.isArray(team) ? team.length : 0;
  if (safeTeamLength <= 0) {
    return 0;
  }

  const normalizedIndex = Number(requestedIndex);
  if (!Number.isInteger(normalizedIndex) || normalizedIndex < 0) {
    return 0;
  }

  return Math.min(normalizedIndex, safeTeamLength - 1);
}

const PlayerList = ({
  onStart,
  settlementMode = false,
  runAuthedRequest,
  onAuthFailure,
  allowRegistrationCancel = false,
}) => {
  const dispatch = useDispatch();
  const showAlert = useAlertDialog();
  const { game, phase, attendance } = useSelector((state) => state.game);
  const [updatingPaymentEmail, setUpdatingPaymentEmail] = React.useState(null);
  const [cancelTargetPlayer, setCancelTargetPlayer] = React.useState(null);
  const [cancelingRegistrationEmail, setCancelingRegistrationEmail] = React.useState(null);

  // Icons are in /public/icons -> reference via PUBLIC_URL
  const ICONS = {
    present: {
      active: `${process.env.PUBLIC_URL}/icons/present_active.svg`,
      inactive: `${process.env.PUBLIC_URL}/icons/present_inactive.svg`,
    },
    absent: {
      active: `${process.env.PUBLIC_URL}/icons/absent_active.svg`,
      inactive: `${process.env.PUBLIC_URL}/icons/absent_inactive.svg`,
    },
    late: {
      active: `${process.env.PUBLIC_URL}/icons/late_active.svg`,
      inactive: `${process.env.PUBLIC_URL}/icons/late_inactive.svg`,
    },
  };

  const persistTeams = async (emailTeams) => {
    try {
      if (typeof runAuthedRequest !== 'function') return;

      await runAuthedRequest(
        (idToken) =>
          axios.patch(
            `${process.env.REACT_APP_API_URL}/games/${game._id}/teams`,
            { teams: emailTeams },
            {
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
              },
            }
          ),
        {
          onAuthFailure,
        }
      );
    } catch (err) {
      console.error('Failed to save new teams', err);
    }
  };

  const handleAttendanceChange = (teamIndex, playerIndex, status) => {
    const player = game.teams?.[teamIndex]?.[playerIndex];
    if (!player?.email) return;
    dispatch(updateAttendance({ key: player.email, status }));
  };

  const handleDragEnd = (result) => {
    if (settlementMode) return;

    const { source, destination } = result;
    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    if (!game || !Array.isArray(game.teams)) return;

    const fromTeam = Number(source.droppableId);
    const toTeam = Number(destination.droppableId);
    const fromIndex = source.index;
    const toIndex = clampDropIndex(game.teams?.[toTeam], destination.index);

    const updatedTeams = game.teams.map((team) => team.slice());

    const movingPlayer = updatedTeams[fromTeam][fromIndex] || null;
    const targetPlayer = updatedTeams[toTeam][toIndex] || null;

    updatedTeams[toTeam][toIndex] = movingPlayer;
    updatedTeams[fromTeam][fromIndex] = targetPlayer || null;

    dispatch(
      movePlayer({
        fromTeam,
        fromIndex,
        toTeam,
        toIndex,
      })
    );

    const emailTeams = updatedTeams.map((team) =>
      team.map((p) => (p?.email ? p.email : null))
    );
    persistTeams(emailTeams);
  };

  const formatDateTime = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString();
  };

  const settleCashPayment = async (playerEmail, nextStatus) => {
    if (!playerEmail) return;
    if (updatingPaymentEmail) return;
    if (typeof runAuthedRequest !== 'function') {
      await showAlert({
        title: 'PAYMENT',
        message: 'Authentication session is missing. Please refresh and try again.',
      });
      return;
    }

    try {
      setUpdatingPaymentEmail(playerEmail);

      const response = await runAuthedRequest(
        (idToken) =>
          axios.patch(
            `${process.env.REACT_APP_API_URL}/games/${game._id}/participants/${encodeURIComponent(playerEmail)}/cash-payment`,
            { paymentStatus: nextStatus },
            {
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
              },
            }
          ),
        {
          onAuthFailure,
        }
      );
      if (!response) return;

      const payload = response?.data || {};
      dispatch(
        updatePlayerPaymentStatus({
          email: playerEmail,
          paymentStatus: payload.paymentStatus || nextStatus,
          paymentMethod: 'cash',
          cashDueAt: payload.cashDueAt ?? null,
          cashPaidAt: payload.cashPaidAt ?? null,
        })
      );

      // Pull server-truth to avoid stale local state.
      dispatch(fetchGame(game._id));
      await showAlert({
        title: 'PAYMENT',
        message: payload.message || 'Payment status updated.',
      });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to update cash payment status.';
      await showAlert({
        title: 'PAYMENT',
        message: msg,
      });
      console.error('Failed to update cash payment status', err);
    } finally {
      setUpdatingPaymentEmail(null);
    }
  };

  const closeCancelRegistrationModal = () => {
    if (cancelingRegistrationEmail) return;
    setCancelTargetPlayer(null);
  };

  const cancelPlayerRegistration = async () => {
    const playerEmail = cancelTargetPlayer?.email;
    if (!playerEmail) return;
    if (cancelingRegistrationEmail) return;
    if (typeof runAuthedRequest !== 'function') {
      await showAlert({
        title: 'REGISTRATION',
        message: 'Authentication session is missing. Please refresh and try again.',
      });
      return;
    }

    try {
      setCancelingRegistrationEmail(playerEmail);

      const response = await runAuthedRequest(
        (idToken) =>
          axios.delete(
            `${process.env.REACT_APP_API_URL}/games/${game._id}/participants/${encodeURIComponent(playerEmail)}/registration`,
            {
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
              },
            }
          ),
        {
          onAuthFailure,
        }
      );
      if (!response) return;

      setCancelTargetPlayer(null);
      dispatch(fetchGame(game._id, { onAuthFailure }));
      await showAlert({
        title: 'REGISTRATION',
        message: response?.data?.message || 'Player registration canceled successfully.',
      });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to cancel player registration.';
      await showAlert({
        title: 'REGISTRATION',
        message: msg,
      });
      console.error('Failed to cancel player registration', err);
    } finally {
      setCancelingRegistrationEmail(null);
    }
  };

  if (!game?.teams) return null;

  return (
    <div className="pl-container">
      {phase === 2 && !settlementMode && (
        <div className="pl-scoreCounter">
          <span className="pl-score">0 : 0</span>
        </div>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="pl-lists">
          {game.teams.map((team, teamIndex) => (
            <Droppable
              droppableId={String(teamIndex)}
              key={teamIndex}
              direction="vertical"
            >
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="pl-team"
                >
                  <h2 className="pl-teamTitle">Team {teamIndex + 1}</h2>

                  {team.map((player, playerIndex) => {
                    const draggableId = player?.email
                      ? `player:${player.email}`
                      : `empty:${teamIndex}:${playerIndex}`;

                    return (
                      <Draggable
                        key={draggableId}
                        draggableId={draggableId}
                        index={playerIndex}
                        isDragDisabled={!player || settlementMode}
                      >
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            className={`pl-row ${
                              snapshot.isDragging ? 'pl-row--dragging' : ''
                            }`}
                          >
                            <span className="pl-rank">{playerIndex + 1}.</span>

                            {!player ? (
                              <span className="pl-empty">Empty Slot</span>
                            ) : (
                              <>
                                <div className="pl-core">
                                  <img
                                    src={player.profilePicture || profilePic}
                                    alt="Player"
                                    className="pl-avatar"
                                  />
                                  <span className="pl-name">
                                    {player.first_name} {player.last_name}
                                  </span>
                                </div>

                                {(settlementMode && (() => {
                                  const isCashPayment =
                                    player.paymentMethod === 'cash' ||
                                    player.paymentStatus === 'pending_cash' ||
                                    player.paymentStatus === 'paid_cash';
                                  const isCreditPayment =
                                    !isCashPayment &&
                                    (player.paymentMethod === 'credit' ||
                                      player.paymentStatus === 'paid_credit' ||
                                      (Array.isArray(player.usedCreditsForThisGame) &&
                                        player.usedCreditsForThisGame.length > 0));

                                  if (!isCashPayment && !isCreditPayment) return null;

                                  if (isCreditPayment) {
                                    return (
                                      <div className="pl-payment">
                                        <span className="pl-paymentBadge pl-paymentBadge--credit">
                                          Credit Paid
                                        </span>
                                      </div>
                                    );
                                  }

                                  return (
                                  <div className="pl-payment">
                                    <span
                                      className={`pl-paymentBadge ${
                                        player.paymentStatus === 'paid_cash'
                                          ? 'pl-paymentBadge--paid'
                                          : 'pl-paymentBadge--pending'
                                      }`}
                                    >
                                      {player.paymentStatus === 'paid_cash'
                                        ? 'Cash Paid'
                                        : 'Cash Pending'}
                                    </span>
                                    {player.paymentStatus !== 'paid_cash' ? (
                                      <button
                                        type="button"
                                        className="pl-paymentBtn"
                                        disabled={updatingPaymentEmail === player.email}
                                        onClick={() => settleCashPayment(player.email, 'paid_cash')}
                                      >
                                        {updatingPaymentEmail === player.email ? 'Saving...' : 'Mark Paid'}
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="pl-paymentBtn pl-paymentBtn--ghost"
                                        disabled={updatingPaymentEmail === player.email}
                                        onClick={() => settleCashPayment(player.email, 'pending_cash')}
                                      >
                                        {updatingPaymentEmail === player.email ? 'Saving...' : 'Mark Pending'}
                                      </button>
                                    )}
                                    {player.paymentStatus === 'pending_cash' && player.cashDueAt && (
                                      <span className="pl-paymentDue">
                                        Due: {formatDateTime(player.cashDueAt)}
                                      </span>
                                    )}
                                  </div>
                                  );
                                })())}

                                {phase === 1 && !settlementMode && allowRegistrationCancel && (
                                  <div className="pl-registrationAction">
                                    <button
                                      type="button"
                                      className="pl-attBtn pl-attBtn--cancelRegistration"
                                      disabled={cancelingRegistrationEmail === player.email}
                                      onClick={() => setCancelTargetPlayer(player)}
                                      aria-label={`Cancel registration for ${player.first_name} ${player.last_name}`}
                                      title="Cancel registration"
                                    >
                                      <img
                                        src={ICONS.absent.inactive}
                                        alt="Cancel registration"
                                        className="pl-attImg"
                                        draggable="false"
                                      />
                                    </button>
                                  </div>
                                )}

                                {phase === 2 && !settlementMode && (
                                  <div className="pl-attendance">
                                    {['present', 'absent', 'late'].map(
                                      (status) => {
                                        const isActive =
                                          attendance[player.email] === status;

                                        const iconSrc = isActive
                                          ? ICONS[status].active
                                          : ICONS[status].inactive;

                                        return (
                                          <button
                                            key={status}
                                            type="button"
                                            className={`pl-attBtn ${
                                              isActive
                                                ? `pl-attBtn--${status}Active`
                                                : ''
                                            }`}
                                            onClick={() =>
                                              handleAttendanceChange(
                                                teamIndex,
                                                playerIndex,
                                                status
                                              )
                                            }
                                            aria-label={`Set ${status}`}
                                            title={status}
                                          >
                                            <img
                                              src={iconSrc}
                                              alt={status}
                                              className="pl-attImg"
                                              draggable="false"
                                            />
                                          </button>
                                        );
                                      }
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}

                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {phase === 2 && !settlementMode && (
        <Button text="Start" onClick={onStart} className="pl-startButton" />
      )}

      <ConfirmDialog
        isOpen={Boolean(cancelTargetPlayer)}
        title="CANCEL REGISTRATION"
        message={
          cancelTargetPlayer
            ? `Are you sure you want to cancel ${cancelTargetPlayer.first_name} ${cancelTargetPlayer.last_name}'s registration for this game?`
            : ''
        }
        confirmText="Yes"
        cancelText="No"
        onConfirm={cancelPlayerRegistration}
        onCancel={closeCancelRegistrationModal}
        busy={Boolean(cancelingRegistrationEmail)}
      />
    </div>
  );
};

export default PlayerList;
